import React, { useState, useRef, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import { useLocation } from 'react-router-dom';

const InvoiceCustom = ({userDatas:userDatas, invoiceType:invoiceType, onShareQuotation   } ) => {
  const invoiceRef = useRef();
  const location = useLocation();
  const orderData = location.state ? location.state : userDatas; // Use orderData from location state if available, otherwise fallback to prop

  // Default empty item template
  const emptyItem = {
    name: '',
    description: '',
    brand: '',
    model: '',
    hsnCode: '',
    quantity: 1,
    rate: 0,
    cgstRate: 2.5,
    sgstRate: 2.5
  };
  // Initialize State
  const [invoiceData, setInvoiceData] = useState({
    invoiceNo: orderData?.invoiceNo || 'MMQ2025-11001',
    invoiceDate: new Date().toISOString().split('T')[0],
    terms: 'Advance',
    poNo: orderData?.uid?.substring(0, 10) || 'SO-000000',
    billTo: {
      name: `${orderData?.buyer_firstname || ''} ${orderData?.buyer_lastname || ''}`.trim() || 'Customer Name',
      address: orderData?.delivery_address?.address || 'Customer Address\nCity, State - ZIP',
      gstin: orderData?.delivery_address?.gstin || 'N/A',
      phone: orderData?.delivery_address?.mobile || 'Phone Number'
    },
    // Array of items instead of single product object
    items: [
      {
        name: orderData?.product_detail?.equip_name || 'Product Name',
        description: orderData?.product_detail?.description || 'Product Description',
        brand: orderData?.product_detail?.brand || 'Brand',
        model: orderData?.product_detail?.model || 'Model',
        hsnCode: orderData?.product_detail?.subcategory || '',
        quantity: 1,
        rate: parseFloat(orderData?.asking_price) || 0,
        cgstRate: 2.5,
        sgstRate: 2.5
      }
    ],
    payment: {
      discount: parseFloat(orderData?.discount) || 0,
      paidAmount: parseFloat(orderData?.paid_amount) || 0,
      bankName: 'IDFC FIRST Bank',
      accountNumber: '10155626447',
      accountName: 'AA MEDIE QUIP LLP',
      ifsc: 'IDFB0021003',
      branch: 'Gurgaon Sector-14'
    }
  });

  // Handle general input changes (Bill To, Payment, Invoice Details)
  const handleInputChange = (section, field, value) => {
    if (section) {
      setInvoiceData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setInvoiceData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Handle Item Row Changes
  const handleItemChange = (index, field, value) => {
    const newItems = [...invoiceData.items];
    
    // Handle numeric fields specially to allow typing
    if (['quantity', 'rate', 'cgstRate', 'sgstRate'].includes(field)) {
       // If value is empty string, keep it as empty string to allow backspace
       if (value === '') {
         newItems[index][field] = ''; 
       } else {
         const num = parseFloat(value);
         newItems[index][field] = isNaN(num) ? 0 : num;
       }
    } else {
       newItems[index][field] = value;
    }

    setInvoiceData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  // Add a new product row
  const addItem = () => {
    setInvoiceData(prev => ({
      ...prev,
      items: [...prev.items, { ...emptyItem }]
    }));
  };

  // Remove a product row
  const removeItem = (index) => {
    if (invoiceData.items.length > 1) {
      const newItems = invoiceData.items.filter((_, i) => i !== index);
      setInvoiceData(prev => ({
        ...prev,
        items: newItems
      }));
    }
  };

  // Calculate totals across all items
  const calculateTotals = () => {
    let subTotal = 0;
    let totalCgst = 0;
    let totalSgst = 0;

    invoiceData.items.forEach(item => {
      const quantity = Number(item.quantity) || 0;
      const rate = Number(item.rate) || 0;
      const cgstRate = Number(item.cgstRate) || 0;
      const sgstRate = Number(item.sgstRate) || 0;

      const basePrice = rate * quantity;
      const cgstAmount = basePrice * (cgstRate / 100);
      const sgstAmount = basePrice * (sgstRate / 100);

      subTotal += basePrice;
      totalCgst += cgstAmount;
      totalSgst += sgstAmount;
    });

    const totalTax = totalCgst + totalSgst;
    const totalAmount = subTotal + totalTax - (Number(invoiceData.payment.discount) || 0);
    const balanceDue = totalAmount - (Number(invoiceData.payment.paidAmount) || 0);

    return {
      subTotal,
      totalCgst,
      totalSgst,
      totalTax,
      totalAmount,
      balanceDue
    };
  };

  const totals = calculateTotals();

  // Number to words function
  const numberToWords = (num) => {
    if (num === 0) return 'Zero Rupees Only';

    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertLessThanOneThousand = (n) => {
      if (n === 0) return '';
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      }
      if (n >= 10) {
        result += teens[n - 10] + ' ';
        n = 0;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result.trim();
    };

    const convertToWords = (n) => {
      if (n === 0) return 'Zero';
      let words = '';
      if (n >= 10000000) {
        words += convertLessThanOneThousand(Math.floor(n / 10000000)) + ' Crore ';
        n %= 10000000;
      }
      if (n >= 100000) {
        words += convertLessThanOneThousand(Math.floor(n / 100000)) + ' Lakh ';
        n %= 100000;
      }
      if (n >= 1000) {
        words += convertLessThanOneThousand(Math.floor(n / 1000)) + ' Thousand ';
        n %= 1000;
      }
      words += convertLessThanOneThousand(n);
      return words.trim();
    };

    let rupeesWords = convertToWords(rupees);
    let paiseWords = convertToWords(paise);

    let result = '';
    if (rupeesWords && rupeesWords !== 'Zero') {
      result += `${rupeesWords} Rupee${rupees === 1 ? '' : 's'}`;
    }

    if (paiseWords && paiseWords !== 'Zero') {
      if (result) result += ' and ';
      result += `${paiseWords} Paise`;
    }

    if (!result) result = 'Zero';
    return result + ' Only';
  };

  // PDF Download Logic
  const downloadPDF = () => {
    const element = invoiceRef.current;
    const inputs = element.querySelectorAll('input, textarea');
    const staticSpans = element.querySelectorAll('.static-text');
    const noPrintElements = element.querySelectorAll('.no-print');

    // Prepare for PDF
    inputs.forEach(input => input.style.display = 'none');
    staticSpans.forEach(span => span.style.display = 'inline');
    noPrintElements.forEach(el => el.style.display = 'none');

    element.style.width = '210mm';
    element.style.height = 'auto';
    element.style.padding = '10mm';

    const opt = {
      margin: [5, 5, 5, 5],
      filename: `${invoiceData.invoiceNo}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true, width: 794 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4', compress: true }
    };

    html2pdf().set(opt).from(element).save().then(() => {
      // Restore styles
      element.style.width = '';
      element.style.height = '';
      element.style.padding = '';
      inputs.forEach(input => input.style.display = '');
      staticSpans.forEach(span => span.style.display = 'none');
      noPrintElements.forEach(el => el.style.display = '');
    });
  };

  // Print Logic
  const handlePrint = () => {
    const element = invoiceRef.current;
    let printContent = element.innerHTML;

    // Helper to escape regex
    const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${invoiceData.invoiceNo}</title>
          <style>
            @media print {
              @page { margin: 0; }
              body { margin: 0; padding: 0; background: white; -webkit-print-color-adjust: exact; }
              .no-print { display: none !important; }
              input, textarea { display: none !important; }
              .static-text { display: inline !important; }
              .invoice-container { border: none !important; width: 100% !important; }
            }
            body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; }
            .invoice-container { max-width: 800px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; }
            
            /* Basic Utility Classes from your component */
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #333; padding: 4px; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .font-semibold { font-weight: 600; }
            .bg-gray-100 { background-color: #f3f4f6; }
            .text-blue-800 { color: #1e40af; }
            .grid { display: grid; }
            .grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
            .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
            .col-span-2 { grid-column: span 2; }
            .border { border: 1px solid #333; }
            .border-t { border-top: 1px solid #333; }
            .border-b { border-bottom: 1px solid #333; }
            .border-r { border-right: 1px solid #333; }
            .p-1 { padding: 4px; } .p-2 { padding: 8px; } .p-3 { padding: 12px; }
            .mt-1 { margin-top: 4px; } .mt-2 { margin-top: 8px; } .mb-1 { margin-bottom: 4px; }
            .w-full { width: 100%; }
            .whitespace-pre-line { white-space: pre-line; }
            
            /* Hide inputs by default in the print window HTML we are generating */
            input, textarea, .no-print { display: none; }
            .static-text { display: inline; }
          </style>
        </head>
        <body onload="window.print(); window.close();">
          <div class="invoice-container">
            ${printContent}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const printStyles = `
    @media print {
      body, html { margin: 0 !important; padding: 0 !important; background: white !important; -webkit-print-color-adjust: exact; }
      body * { visibility: hidden; }
      #invoice-print-area, #invoice-print-area * { visibility: visible; }
      #invoice-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
      .no-print, .print-hidden, input, textarea { display: none !important; }
      .static-text { display: inline !important; }
      .invoice-container { border: none !important; box-shadow: none !important; }
    }
    input, textarea { display: inline; width: 100%; border: 1px dashed #ccc; background: transparent; padding: 2px 4px; font-size: 11px; font-family: inherit; resize: none; }
    input:focus, textarea:focus { outline: none; border-color: #3b82f6; }
    .static-text { display: none; font-size: 11px; }
    .invoice-input { min-width: 80px; }
    .address-textarea { min-height: 60px; white-space: pre-line; }
    .numeric-input { width: 70px; text-align: center; }
    .small-input { width: 60px; text-align: center; }
  `;

  const getPDFBlob = () => {
  return new Promise((resolve, reject) => {
    const element = invoiceRef.current;
    const inputs = element.querySelectorAll('input, textarea');
    const staticSpans = element.querySelectorAll('.static-text');
    const noPrintElements = element.querySelectorAll('.no-print');

    // Prepare for PDF
    inputs.forEach(input => input.style.display = 'none');
    staticSpans.forEach(span => span.style.display = 'inline');
    noPrintElements.forEach(el => el.style.display = 'none');

    element.style.width = '210mm';
    element.style.height = 'auto';
    element.style.padding = '10mm';

    const opt = {
      margin: [5, 5, 5, 5],
      filename: `${invoiceData.invoiceNo}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true, width: 794 },
      jsPDF: { orientation: 'portrait', unit: 'mm', format: 'a4', compress: true },
      // Add output: 'blob' to get blob
      output: 'blob'
    };

    html2pdf().set(opt).from(element).outputPdf('blob').then((pdfBlob) => {
      // Restore styles
      element.style.width = '';
      element.style.height = '';
      element.style.padding = '';
      inputs.forEach(input => input.style.display = '');
      staticSpans.forEach(span => span.style.display = 'none');
      noPrintElements.forEach(el => el.style.display = '');
      resolve(pdfBlob);
    }).catch(err => {
      // Restore styles
      element.style.width = '';
      element.style.height = '';
      element.style.padding = '';
      inputs.forEach(input => input.style.display = '');
      staticSpans.forEach(span => span.style.display = 'none');
      noPrintElements.forEach(el => el.style.display = '');
      reject(err);
    });
  });
};

const handleShareQuotation = async () => {
  try {
    const pdfBlob = await getPDFBlob();
    if (onShareQuotation) {
      onShareQuotation(pdfBlob);
    } else {
      console.warn('No onShareQuotation callback provided');
    }
  } catch (err) {
    console.error('Failed to generate PDF:', err);
    // Optionally show an error toast
  }
};

  return (
    <div className="min-h-screen bg-gray-100 py-4 px-2">
      <style>{printStyles}</style>

      <div id="invoice-print-area" className="max-w-3xl mx-auto bg-white shadow-xl border border-gray-300">

        {/* Action Buttons */}
        {invoiceType !== 'viachat' ? (
          <div className="flex justify-between items-center p-3 bg-gray-50 border-b no-print">
            <div className="flex gap-2">
              <button onClick={downloadPDF} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Download PDF</button>
              <button onClick={handlePrint} className="px-3 py-1.5 bg-[#019c89] text-white text-sm rounded hover:bg-teal-700">Print</button>
            </div>
            <div className="text-sm text-gray-600">Edit fields to customize. Add/Remove rows supported.</div>
          </div>
        ):(
          <div className="flex justify-between items-center p-3 bg-gray-50 border-b no-print">
            <div></div>
            <button onClick={handleShareQuotation} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">Share Quotation</button>
          </div>
        )}

        {/* INVOICE CONTENT */}
        <div ref={invoiceRef} className="p-4 text-[12px]">

          {/* TOP HEADER */}
          <div className="grid grid-cols-3 border border-gray-300">
            {/* Left Box */}
            <div className="col-span-2 border-r border-gray-300 p-3">
              <img src={process.env.PUBLIC_URL + "/EquipR.png"} alt="EquipMedy Logo" className="h-12 w-[300px] mb-1 object-contain " />
              <div className="leading-4 text-[11px]">
                <p className="font-semibold text-blue-800">EquipMedy</p>
                <p>B-99, 5th Floor, Phase-I Panchsheel Park, Malviya Nagar</p>
                <p>Near Triveni Complex, New Delhi - 110017</p>
                <p>Corporate Address: F-300, 2nd Floor, Sector 63, Noida, UP-201301</p>
                <p className="mt-1">Email: info@equipmedy.in | Mobile: +91-8595903932</p>
                <p className="font-semibold mt-1">GSTIN: 07ACCFA9252A1ZS</p>
              </div>
            </div>

            {/* Right Box - Invoice Details */}
            <div className="p-3 flex flex-col justify-center text-right">
              <p className="text-xl font-bold tracking-wide">PERFORMA INVOICE/QUOTATION</p>
              <table className="text-[11px] w-full mt-2">
                <tbody>
                  <tr>
                    <td className="font-semibold border-0 p-1">Invoice No:</td>
                    <td className="border-0 p-1">
                      <input type="text" value={invoiceData.invoiceNo} onChange={(e) => handleInputChange(null, 'invoiceNo', e.target.value)} className="invoice-input text-right" />
                      <span className="static-text">{invoiceData.invoiceNo}</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-semibold border-0 p-1">Date:</td>
                    <td className="border-0 p-1">
                      <input type="date" value={invoiceData.invoiceDate} onChange={(e) => handleInputChange(null, 'invoiceDate', e.target.value)} className="invoice-input text-right" />
                      <span className="static-text">{invoiceData.invoiceDate}</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-semibold border-0 p-1">Terms:</td>
                    <td className="border-0 p-1">
                      <input type="text" value={invoiceData.terms} onChange={(e) => handleInputChange(null, 'terms', e.target.value)} className="invoice-input text-right" />
                      <span className="static-text">{invoiceData.terms}</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="font-semibold border-0 p-1">P.O.#:</td>
                    <td className="border-0 p-1">
                      <input type="text" value={invoiceData.poNo} onChange={(e) => handleInputChange(null, 'poNo', e.target.value)} className="invoice-input text-right" />
                      <span className="static-text">{invoiceData.poNo}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* BILL TO */}
          <div className="border border-t-0 border-gray-300 p-3">
            <p className="font-bold mb-1">Bill To</p>
            <p>
              <input type="text" value={invoiceData.billTo.name} onChange={(e) => handleInputChange('billTo', 'name', e.target.value)} className="invoice-input font-semibold w-1/2" placeholder="Customer Name" />
              <span className="static-text font-semibold">{invoiceData.billTo.name}</span>
            </p>
            {/* <textarea value={invoiceData.billTo.address} onChange={(e) => handleInputChange('billTo', 'address', e.target.value)} className="address-textarea w-full text-gray-700 text-[11px]" placeholder="Address" /> */}
            <textarea value={invoiceData.billTo.address} onChange={(e) => handleInputChange('billTo', 'address', e.target.value)} className="address-textarea w-full font-semibold text-[11px]" placeholder="Address" />
            {/* <pre className="static-text whitespace-pre-line text-[11px] text-gray-700">{invoiceData.billTo.address}</pre> */}
            <pre className="static-text text-[11px] font-semibold">{invoiceData.billTo.address}</pre>

            <p className="mt-1 text-[11px]">
              <span className="font-semibold">Phone:</span>
              <input type="text" value={invoiceData.billTo.phone} onChange={(e) => handleInputChange('billTo', 'phone', e.target.value)} className="invoice-input ml-1" />
              <span className="static-text ml-1">{invoiceData.billTo.phone}</span>
            </p>
            <p className="text-[11px]">
              <span className="font-semibold">GSTIN:</span>
              <input type="text" value={invoiceData.billTo.gstin} onChange={(e) => handleInputChange('billTo', 'gstin', e.target.value)} className="invoice-input ml-1" />
              <span className="static-text ml-1">{invoiceData.billTo.gstin}</span>
            </p>
          </div>

          {/* ITEMS TABLE */}
          <table className="w-full mt-3 border border-gray-300 text-[11px]">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-1 w-8">#</th>
                <th className="border p-1 text-left">Item & Description</th>
                <th className="border p-1 w-20">HSN/SAC</th>
                <th className="border p-1 w-10">Qty</th>
                <th className="border p-1 w-20">Rate</th>
                <th className="border p-1 w-14">CGST %</th>
                <th className="border p-1 w-14">SGST %</th>
                <th className="border p-1 w-24">Amount</th>
                <th className="border p-1 w-8 no-print">Act</th>
              </tr>
            </thead>
            <tbody>
              {invoiceData.items.map((item, index) => {
                // Calculate row specifics for display
                const q = Number(item.quantity) || 0;
                const r = Number(item.rate) || 0;
                const cRate = Number(item.cgstRate) || 0;
                const sRate = Number(item.sgstRate) || 0;
                const amount = q * r;
                const cgstAmt = amount * (cRate / 100);
                const sgstAmt = amount * (sRate / 100);
                const totalRow = amount + cgstAmt + sgstAmt;

                return (
                  <tr key={index}>
                    <td className="border p-1 text-center">{index + 1}</td>
                    
                    {/* Description */}
                    <td className="border p-1">
                      <div className="mb-1">
                        <input type="text" value={item.name} onChange={(e) => handleItemChange(index, 'name', e.target.value)} className="invoice-input font-semibold w-full" placeholder="Product Name" />
                        <span className="static-text font-semibold">{item.name}</span>
                      </div>
                      <textarea value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} className="address-textarea w-full text-gray-600 mb-1" placeholder="Description" rows="1" />
                      <span className="static-text text-gray-600 block mb-1">{item.description}</span>
                      
                      <div className="text-[10px] text-gray-500 flex items-center">
                        Brand: 
                        <input type="text" value={item.brand} onChange={(e) => handleItemChange(index, 'brand', e.target.value)} className="small-input ml-1 mr-1" placeholder="Brand" />
                        <span className="static-text ml-1 mr-1">{item.brand}</span>
                        | Model: 
                        <input type="text" value={item.model} onChange={(e) => handleItemChange(index, 'model', e.target.value)} className="small-input ml-1" placeholder="Model" />
                        <span className="static-text ml-1">{item.model}</span>
                      </div>
                    </td>

                    {/* HSN */}
                    <td className="border p-1 text-center">
                      <input type="text" value={item.hsnCode} onChange={(e) => handleItemChange(index, 'hsnCode', e.target.value)} className="small-input text-center" />
                      <span className="static-text">{item.hsnCode || '—'}</span>
                    </td>

                    {/* Quantity */}
                    <td className="border p-1 text-center">
                      <input type="number" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} className="small-input text-center" />
                      <span className="static-text">{item.quantity}</span>
                    </td>

                    {/* Rate */}
                    <td className="border p-1 text-right">
                      <input type="number" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', e.target.value)} className="numeric-input ml-1 text-right" />
                      <span className="static-text">₹ {Number(item.rate).toFixed(2)}</span>
                    </td>

                    {/* CGST */}
                    <td className="border p-1 text-center">
                      <input type="number" value={item.cgstRate} onChange={(e) => handleItemChange(index, 'cgstRate', e.target.value)} className="small-input text-center" />
                      <span className="static-text">{item.cgstRate}%</span>
                      <br />
                      <span className="text-gray-500 text-[9px]">₹ {cgstAmt.toFixed(2)}</span>
                    </td>

                    {/* SGST */}
                    <td className="border p-1 text-center">
                      <input type="number" value={item.sgstRate} onChange={(e) => handleItemChange(index, 'sgstRate', e.target.value)} className="small-input text-center" />
                      <span className="static-text">{item.sgstRate}%</span>
                      <br />
                      <span className="text-gray-500 text-[9px]">₹ {sgstAmt.toFixed(2)}</span>
                    </td>

                    {/* Total Amount */}
                    <td className="border p-1 text-right font-semibold">
                      ₹ {totalRow.toFixed(2)}
                    </td>

                    {/* Actions (Remove) */}
                    <td className="border p-1 text-center no-print">
                      <button onClick={() => removeItem(index)} className="text-red-500 hover:text-red-700 font-bold px-2">×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Add Row Button */}
          <div className="mt-2 no-print">
            <button onClick={addItem} className="text-blue-600 text-[11px] font-bold hover:underline flex items-center gap-1">
              <span className="text-lg">+</span> Add New Item
            </button>
          </div>

          {/* TOTAL SECTION */}
          <div className="mt-2 text-right text-[12px]">
            <p>Sub Total: <span className="font-semibold">₹ {totals.subTotal.toFixed(2)}</span></p>
            <p>Total CGST: ₹ {totals.totalCgst.toFixed(2)}</p>
            <p>Total SGST: ₹ {totals.totalSgst.toFixed(2)}</p>

            <p>
              Discount: -
              <input type="number" value={invoiceData.payment.discount} onChange={(e) => handleInputChange('payment', 'discount', parseFloat(e.target.value) || 0)} className="small-input ml-1 text-red-600 text-right" />
              <span className="static-text ml-1">₹ {Number(invoiceData.payment.discount).toFixed(2)}</span>
            </p>

            <p className="font-bold mt-1 text-[13px]">Total: ₹ {totals.totalAmount.toFixed(2)}</p>

            <p>
              Payment Made: -
              <input type="number" value={invoiceData.payment.paidAmount} onChange={(e) => handleInputChange('payment', 'paidAmount', parseFloat(e.target.value) || 0)} className="small-input ml-1 text-right" />
              <span className="static-text ml-1">₹ {Number(invoiceData.payment.paidAmount).toFixed(2)}</span>
            </p>

            <p className="font-bold text-blue-600 mt-1">Balance Due: ₹ {Math.abs(totals.balanceDue).toFixed(2)}</p>

            <div className='flex items-center justify-between mt-2 border-t pt-2'>
              <div className="font-bold text-[11px] text-gray-800">Amount in Words:</div>
              <div className="text-[10px] italic bg-gray-50 p-1 rounded max-w-md text-right">
                {numberToWords(totals.totalAmount)}
              </div>
            </div>
          </div>

          {/* BANK DETAILS + SIGNATURE */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="border border-gray-300 p-2 text-[11px]">
              <p className="font-bold mb-1">Company's Bank Details</p>
              <p className="font-semibold">IDFC FIRST Bank</p>
              <p>Account: 10155626447</p>
              <p>Name: AA MEDIE QUIP LLP</p>
              <p>IFSC: IDFB0021003</p>
              <p>Branch: Gurgaon Sector-14</p>
            </div>
            <div className="border border-gray-300 p-2 text-center flex flex-col justify-end">
              <div className="mt-8">
                <p className="font-semibold">Authorized Signature</p>
              </div>
            </div>
          </div>

          {/* TERMS & FOOTER */}
          <div className='flex items-end justify-between mt-2'>
            <div className="mt-4 text-[10px] leading-4">
              <p className="font-semibold">Terms & Conditions</p>
              <p>1. Goods once sold will not be taken back.</p>
              <p>2. Warranty excluded for unauthorized repairs.</p>
              <p>3. Buyer will be personally liable to pay cheque bounce charges.</p>
            </div>
            <div>
              <img src={process.env.PUBLIC_URL + "/phonepayQr.jpg"} alt="QR Code" className="w-[100px] h-[100px] object-contain" />
            </div>
          </div>

          <div className="text-center text-[10px] mt-3 border-t pt-2 text-gray-500">
            <a href="https://www.equipmedy.com" className="text-blue-600 no-underline">www.equipmedy.com</a>
          </div>

        </div>
      </div>
    </div>
  );
};

export default InvoiceCustom;